// src/features/simulators/kiosk/hooks/useKioskSimulation.ts

import { useCallback } from 'react';
import type { AnalyticsClient } from '../../shared/utils/analyticsClient';
import type { LogEntry } from '../../shared/types/simulator.types';
import type { SimulationSummary } from '../../shared/hooks/useSimulatorWizard';
import type { KioskConfigValues, KioskJourneyType } from '../components/KioskConfigForm';
import {
  JourneyEventCode,
  KeyDeliveryMethod,
  SectionCode,
  GuestType,
  createCorrelatedEvent,
} from '../../shared/types/journey.types';
import {
  createJourneyState,
  generateRoomNumber,
  generatePaymentData,
  generateSectionDuration,
  generateGuestName,
} from '../../shared/utils/journeyDataGenerator';

type LogFn = (type: LogEntry['type'], message: string, details?: any) => void;

const ERROR_RATE = 0.05;
const ABANDONMENT_RATE = 0.03;

/**
 * Get the human-readable action name for a section
 */
const getSectionAction = (section: SectionCode): string => {
  const actions: Record<string, string> = {
    [SectionCode.S]: 'searchReservation',
    [SectionCode.R]: 'selectRoom',
    [SectionCode.I]: 'guestIdentification',
    [SectionCode.F]: 'digitalSignature',
    [SectionCode.U]: 'upsellOffers',
    [SectionCode.P]: 'processPayment',
    [SectionCode.K]: 'keyDelivery',
  };
  return actions[section] || 'processing';
};

/**
 * Check whether a key delivery method is physical
 */
const isPhysicalKey = (method: KeyDeliveryMethod): boolean => {
  return [
    KeyDeliveryMethod.MAGNETIC_CARD,
    KeyDeliveryMethod.RFID_CARD,
    KeyDeliveryMethod.CONVENTIONAL_KEY,
  ].includes(method);
};

/**
 * Small delay helper (pure, no React hooks)
 */
const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const useKioskSimulation = () => {
  const simulate = useCallback(
    async (
      client: AnalyticsClient,
      addLog: LogFn,
      deviceId: string,
      hotelId: string,
      config: KioskConfigValues,
    ): Promise<SimulationSummary> => {
      const startTime = Date.now();
      let totalEvents = 0;
      let successful = 0;
      let failed = 0;
      let abandoned = 0;

      /**
       * Send a single event, track counts, and log on failure.
       */
      const sendEvent = async (event: any): Promise<void> => {
        try {
          await client.sendEvent({ ...event, channel: 'Kiosk' });
          totalEvents++;
        } catch (err: any) {
          addLog('error', `Failed to send event: ${err.message}`);
          throw err;
        }
      };

      /**
       * Process a section: SECTION_ENTER -> (optional payment) -> SECTION_EXIT
       * Returns false if payment failed (thrown by caller if needed).
       */
      const processSection = async (
        section: SectionCode,
        journey: ReturnType<typeof createJourneyState>,
      ): Promise<boolean> => {
        const duration = generateSectionDuration(section);

        // SECTION_ENTER
        await sendEvent(
          createCorrelatedEvent(
            JourneyEventCode.SECTION_ENTER,
            deviceId,
            hotelId,
            journey,
            { sectionCode: section, action: getSectionAction(section) },
          ),
        );

        journey.currentSection = section;
        addLog('info', `Processing section ${section} (${duration}s)`);
        await delay(duration * 100);

        // Special handling for payment section
        if (section === SectionCode.P) {
          const payment = generatePaymentData();

          if (Math.random() < ERROR_RATE) {
            await sendEvent(
              createCorrelatedEvent(
                JourneyEventCode.PM_ERROR,
                deviceId,
                hotelId,
                journey,
                {
                  sectionCode: section,
                  errorCode: 'CARD_DECLINED',
                  amount: payment.amount.toString(),
                },
              ),
            );
            addLog('error', `Payment failed: CARD_DECLINED`);
            failed++;
            return false;
          }

          await sendEvent(
            createCorrelatedEvent(
              JourneyEventCode.PM_SUCCESS,
              deviceId,
              hotelId,
              journey,
              {
                sectionCode: section,
                amount: payment.amount.toString(),
                paymentMethod: payment.method,
                operationId: payment.operationId,
              },
            ),
          );
          addLog('success', `Payment successful: ${payment.amount} EUR`);
        }

        // SECTION_EXIT
        await sendEvent(
          createCorrelatedEvent(
            JourneyEventCode.SECTION_EXIT,
            deviceId,
            hotelId,
            journey,
            { sectionCode: section, duration: duration.toString() },
          ),
        );

        journey.completedSections.push(section);
        journey.currentSection = undefined;
        return true;
      };

      /**
       * Send CI_COMP, key delivery, sendCheckIn, updateJourney
       */
      const completeCheckIn = async (
        journey: ReturnType<typeof createJourneyState>,
        extraMetadata: Record<string, string> = {},
        completionDeviceId: string = deviceId,
      ): Promise<void> => {
        // Ensure room is assigned
        if (!journey.roomNumber) {
          journey.roomNumber = generateRoomNumber();
          addLog('info', `Room assigned: ${journey.roomNumber}`);
        }

        const metadata: Record<string, string> = {
          pmsRegistered: 'true',
          keyDeliveryMethod: config.keyDeliveryMethod,
          ...extraMetadata,
        };

        // Handle QR dispenser
        if (config.keyDeliveryMethod === KeyDeliveryMethod.QR_DISPENSER) {
          metadata.qrGenerated = 'true';
          metadata.qrType = 'UniqueId';
          metadata.qrUniqueId = `QR-${Date.now()}`;
          journey.qrUniqueId = metadata.qrUniqueId;
          addLog('info', `QR generated for dispenser: ${metadata.qrUniqueId}`);
        }

        // CI_COMP event
        const ciCompEvent = createCorrelatedEvent(
          JourneyEventCode.CI_COMP,
          completionDeviceId,
          hotelId,
          journey,
          metadata,
        );
        ciCompEvent.roomNumber = journey.roomNumber;
        // Full name, top-level like a real kiosk: Insight reduces it to «Juan G.».
        const guest = generateGuestName();
        ciCompEvent.guestName = guest.full;

        await sendEvent(ciCompEvent);
        journey.checkInCompleted = true;
        successful++;

        // sendCheckIn
        await client.sendCheckIn({
          reservationNumber: journey.reservationNumber || '',
          guestNameMasked: guest.full,
          documentMasked: 'A1234***',
          roomNumber: journey.roomNumber,
          amountCharged: 0,
          paymentMethod: 'N/A',
          paymentOperationId: 'N/A',
          deviceId: completionDeviceId,
          hotelId,
          channel: 'Kiosk',
          type: journey.isWalking ? 'Walking' : 'Reservation',
        });

        // updateJourney: CheckInCompleted
        await client.updateJourney({
          reservationNumber: journey.reservationNumber || '',
          alternateReservationNumbers: journey.alternateReservationNumbers || [],
          hotelId,
          channel: 'Kiosk',
          eventType: 'CheckInCompleted',
          timestamp: new Date().toISOString(),
        });

        // Key delivery for physical keys
        if (isPhysicalKey(config.keyDeliveryMethod)) {
          // Key delivery event
          if (Math.random() < ERROR_RATE) {
            await sendEvent(
              createCorrelatedEvent(
                JourneyEventCode.KD_ERROR,
                completionDeviceId,
                hotelId,
                journey,
                { errorCode: 'DISPENSER_ERROR', roomNumber: journey.roomNumber },
              ),
            );
            addLog('error', `Key dispense error for room ${journey.roomNumber}`);
            failed++;
          } else {
            await sendEvent(
              createCorrelatedEvent(
                JourneyEventCode.KD_SUCCESS,
                completionDeviceId,
                hotelId,
                journey,
                { keyType: 'physical', roomNumber: journey.roomNumber },
              ),
            );
            journey.keyDispensed = true;
            addLog('success', `Key dispensed for room ${journey.roomNumber}`);
          }

          await client.updateJourney({
            reservationNumber: journey.reservationNumber || '',
            alternateReservationNumbers: journey.alternateReservationNumbers || [],
            hotelId,
            channel: 'Kiosk',
            eventType: 'KeyDispensed',
            timestamp: new Date().toISOString(),
          });
        }
      };

      // ---------------------------------------------------------------
      // Journey-type dispatch
      // ---------------------------------------------------------------
      try {
        switch (config.journeyType as KioskJourneyType) {
          // ============================================================
          // NEW CHECK-IN
          // ============================================================
          case 'new_checkin': {
            const reservationToUse =
              !config.autoGenerateReservation && config.reservationNumber
                ? config.reservationNumber
                : undefined;

            const journey = createJourneyState({
              guestType: GuestType.RESERVATION,
              hasPreCheckIn: false,
              reservationNumber: reservationToUse,
            });

            addLog('info', `Starting NEW CHECK-IN | Reservation: ${journey.reservationNumber}`);

            // CI_START
            await sendEvent(
              createCorrelatedEvent(
                JourneyEventCode.CI_START,
                deviceId,
                hotelId,
                journey,
                { checkInType: GuestType.RESERVATION, hasPreCheckIn: 'false' },
              ),
            );
            journey.checkInStarted = true;

            // Process configured sections (S, I, F, U, P)
            for (const section of config.sectionsToProcess) {
              if (section === SectionCode.R) continue; // R not for reservation guests
              const ok = await processSection(section, journey);
              if (!ok) {
                return {
                  success: false,
                  totalEvents,
                  successful,
                  failed,
                  abandoned,
                  duration: Date.now() - startTime,
                  error: 'Payment failed',
                };
              }
            }

            // Complete
            await completeCheckIn(journey);
            addLog('success', `Check-in COMPLETE | Room: ${journey.roomNumber} | ${Date.now() - startTime}ms`);
            break;
          }

          // ============================================================
          // CONTINUE PRE CHECK-IN
          // ============================================================
          case 'continue_precheckin': {
            const reservationToUse =
              !config.autoGenerateReservation && config.reservationNumber
                ? config.reservationNumber
                : undefined;

            const journey = createJourneyState({
              guestType: GuestType.RESERVATION,
              hasPreCheckIn: true,
              roomNumber: generateRoomNumber(), // Usually assigned during pre-check-in
              reservationNumber: reservationToUse,
            });

            journey.preCheckInStarted = true;
            journey.preCheckInCompleted = true;

            addLog('info', `CONTINUING PRE-CHECK-IN | Reservation: ${journey.reservationNumber} | Room: ${journey.roomNumber}`);

            // CI_START with pre-check-in flag
            await sendEvent(
              createCorrelatedEvent(
                JourneyEventCode.CI_START,
                deviceId,
                hotelId,
                journey,
                {
                  hasPreCheckIn: 'true',
                  continueFromPC: 'true',
                  roomNumber: journey.roomNumber!,
                },
              ),
            );
            journey.checkInStarted = true;

            // Minimal sections: just S to verify reservation
            await processSection(SectionCode.S, journey);

            // Complete
            await completeCheckIn(journey, { continuedFromPreCheckIn: 'true' });
            addLog('success', `Pre-check-in continued successfully | Room: ${journey.roomNumber} | ${Date.now() - startTime}ms`);
            break;
          }

          // ============================================================
          // WALKING GUEST
          // ============================================================
          case 'walking_guest': {
            const journey = createJourneyState({
              guestType: GuestType.WALKING,
              hasPreCheckIn: false,
            });

            addLog('info', `Starting WALKING GUEST CHECK-IN`);
            addLog('warning', `No reservation number (walking guest)`);

            // CI_START with walking flag
            await sendEvent(
              createCorrelatedEvent(
                JourneyEventCode.CI_START,
                deviceId,
                hotelId,
                journey,
                { isWalking: 'true', checkInType: GuestType.WALKING },
              ),
            );
            journey.checkInStarted = true;
            journey.correlationWindow = new Date();

            // Section R first (room selection for walking guests)
            await processSection(SectionCode.R, journey);
            journey.roomNumber = generateRoomNumber();
            addLog('info', `Walking guest selected room: ${journey.roomNumber}`);

            // Remaining sections (skip S since no reservation, R already done)
            const walkingSections = config.sectionsToProcess.filter(
              (s) => s !== SectionCode.S && s !== SectionCode.R,
            );
            for (const section of walkingSections) {
              const ok = await processSection(section, journey);
              if (!ok) {
                return {
                  success: false,
                  totalEvents,
                  successful,
                  failed,
                  abandoned,
                  duration: Date.now() - startTime,
                  error: 'Payment failed',
                };
              }
            }

            // Complete
            await completeCheckIn(journey, { isWalking: 'true' });
            addLog('success', `Walking guest check-in COMPLETE | Room: ${journey.roomNumber} | ${Date.now() - startTime}ms`);
            break;
          }

          // ============================================================
          // HYBRID ABANDONMENT
          // ============================================================
          case 'hybrid_abandonment': {
            const reservationToUse =
              !config.autoGenerateReservation && config.reservationNumber
                ? config.reservationNumber
                : undefined;

            const journey = createJourneyState({
              guestType: GuestType.RESERVATION,
              hasPreCheckIn: false,
              reservationNumber: reservationToUse,
            });

            addLog('info', `Starting HYBRID ABANDONMENT FLOW | Reservation: ${journey.reservationNumber}`);

            // CI_START
            await sendEvent(
              createCorrelatedEvent(
                JourneyEventCode.CI_START,
                deviceId,
                hotelId,
                journey,
                { checkInType: GuestType.RESERVATION },
              ),
            );
            journey.checkInStarted = true;

            // Process some sections before abandoning
            const sectionsBeforeAbandon = [SectionCode.S, SectionCode.I];
            for (const section of sectionsBeforeAbandon) {
              await processSection(section, journey);
            }

            // Pick a random section to abandon at
            const abandonableSections = [SectionCode.F, SectionCode.U, SectionCode.P];
            const abandonSection =
              abandonableSections[Math.floor(Math.random() * abandonableSections.length)];

            addLog('warning', `Guest abandons at section ${abandonSection}`);
            abandoned++;

            // AB event
            await sendEvent(
              createCorrelatedEvent(
                JourneyEventCode.AB,
                deviceId,
                hotelId,
                journey,
                {
                  sectionCode: abandonSection,
                  abandonReason: 'user_exit',
                  timeInSection: '30',
                  completedSections: journey.completedSections.join(','),
                },
              ),
            );

            addLog('info', 'Guest goes to reception to complete check-in');
            await delay(3000);

            // Complete at reception with a different device
            journey.roomNumber = generateRoomNumber();

            await completeCheckIn(
              journey,
              {
                completedAtReception: 'true',
                previousAbandonment: 'true',
                keyDeliveryMethod: KeyDeliveryMethod.MANUAL,
              },
              'reception-01',
            );

            addLog('success', `Check-in completed at reception after abandonment | Room: ${journey.roomNumber}`);
            break;
          }

          default:
            throw new Error(`Unknown journey type: ${config.journeyType}`);
        }

        return {
          success: true,
          totalEvents,
          successful,
          failed,
          abandoned,
          duration: Date.now() - startTime,
        };
      } catch (error: any) {
        addLog('error', `Simulation error: ${error.message}`);
        return {
          success: false,
          totalEvents,
          successful,
          failed,
          abandoned,
          duration: Date.now() - startTime,
          error: error.message,
        };
      }
    },
    [],
  );

  return { simulate };
};
